/**
 * The assurance strip's own load, shared by every view that shows the lanes.
 *
 * Lifted out of CommandDeck unchanged. Fetched separately from the dashboard payload because the
 * two answer different questions: the payload says what the vault holds, this says whether the
 * lanes that fill it are still working. Folding the second into the first would let a broken lane
 * take the whole board down with it, which is exactly backwards.
 *
 * NOTHING HERE MAY THROW UP THE TREE. `/api/mo/assurance` answers even while ingestion is off, but
 * it sits behind the session wall, and an expired session returns `{ error: 'unauthorized' }` as
 * valid JSON with a 401. Hence the status check and the shape check, which is what `api.ts`'s own
 * `get` does for every other route: on anything unexpected the strip simply does not render.
 *
 * POLLED, because a strip that only reads once is a photograph of the lanes rather than a view of
 * them, and the lamps would never light. Thirty seconds against lanes whose fastest cadence is the
 * fifteen minute mail sweep: often enough that a report is noticed while it is still worth
 * noticing, rare enough to be nothing on a board he leaves open all day.
 */
import { useEffect, useState } from 'react';
import type { AssuranceView } from '@rbv/shared';

export function useAssurance(): AssuranceView | null {
  const [view, setView] = useState<AssuranceView | null>(null);
  useEffect(() => {
    let live = true;
    const read = () =>
      void fetch('/api/mo/assurance', { credentials: 'same-origin' })
        .then((r) => (r.ok ? (r.json() as Promise<unknown>) : null))
        .then((v) => {
          if (!live) return;
          if (v !== null && typeof v === 'object' && Array.isArray((v as AssuranceView).lanes)) {
            setView(v as AssuranceView);
          }
        })
        .catch(() => {
          /* Keep the last good reading. It must never break the view. */
        });
    read();
    const t = setInterval(read, 30_000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, []);
  return view;
}
